import { useState, useEffect } from 'react';
import { FiCheck, FiX, FiClock, FiUser, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import { timeEntryModificationsAPI } from '../../services/api';
import { useApp } from '../../context/AppContext';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const ModificationRequestsPanel = ({ onRequestsChange }) => {
  const { addNotification } = useApp();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setIsLoading(true);
      const data = await timeEntryModificationsAPI.getPending();
      setRequests(data);
      if (onRequestsChange) {
        onRequestsChange(data.length);
      }
    } catch (error) {
      console.error('Error loading modification requests:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar las solicitudes de modificacion'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      return format(parseISO(dateTimeString), 'dd/MM/yyyy HH:mm', { locale: es });
    } catch (error) {
      return dateTimeString;
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setProcessingId(requestId);
      await timeEntryModificationsAPI.review(requestId, { approved: true });
      addNotification({
        type: 'success',
        message: 'Solicitud aprobada correctamente'
      });
      loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      addNotification({
        type: 'error',
        message: 'Error al aprobar la solicitud'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    try {
      setProcessingId(selectedRequest.id);
      await timeEntryModificationsAPI.review(selectedRequest.id, {
        approved: false,
        rejection_reason: rejectionReason || null
      });
      addNotification({
        type: 'success',
        message: 'Solicitud rechazada'
      });
      setRejectModalOpen(false);
      setSelectedRequest(null);
      loadRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      addNotification({
        type: 'error',
        message: 'Error al rechazar la solicitud'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const renderChangeComparison = (label, original, requested) => {
    if (!requested) return null;
    const originalFormatted = formatDateTime(original);
    const requestedFormatted = formatDateTime(requested);

    if (originalFormatted === requestedFormatted) return null;

    return (
      <div className="text-sm">
        <span className="text-gray-500">{label}:</span>
        <div className="flex items-center gap-2">
          <span className="text-red-600 line-through">{originalFormatted}</span>
          <span className="text-gray-400">-&gt;</span>
          <span className="text-green-600 font-medium">{requestedFormatted}</span>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8">
          <FiCheck className="mx-auto text-green-500 text-4xl mb-3" />
          <p className="text-gray-500">No hay solicitudes de modificacion pendientes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FiAlertCircle className="text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">
            Solicitudes de Modificacion Pendientes ({requests.length})
          </h3>
        </div>
        <button
          onClick={loadRequests}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Actualizar"
        >
          <FiRefreshCw className="h-5 w-5" />
        </button>
      </div>

      <div className="divide-y divide-gray-200">
        {requests.map((request) => (
          <div key={request.id} className="p-6 hover:bg-gray-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <FiUser className="text-gray-400" />
                  <span className="font-medium text-gray-900">{request.requester_name}</span>
                  <span className="text-gray-400">|</span>
                  <FiClock className="text-gray-400" />
                  <span className="text-sm text-gray-500">
                    Solicitado: {formatDateTime(request.created_at)}
                  </span>
                </div>

                <div className="space-y-1 mb-3">
                  {renderChangeComparison('Entrada', request.original_clock_in_time, request.requested_clock_in_time)}
                  {renderChangeComparison('Salida', request.original_clock_out_time, request.requested_clock_out_time)}
                  {request.requested_notes !== null && request.requested_notes !== request.original_notes && (
                    <div className="text-sm">
                      <span className="text-gray-500">Notas:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-red-600 line-through">{request.original_notes || '(vacio)'}</span>
                        <span className="text-gray-400">-&gt;</span>
                        <span className="text-green-600 font-medium">{request.requested_notes || '(vacio)'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {request.reason && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <span className="text-blue-700 font-medium">Motivo: </span>
                    <span className="text-blue-600">{request.reason}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => handleApprove(request.id)}
                  disabled={processingId === request.id}
                  className="flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <FiCheck className="h-4 w-4" />
                  <span>Aprobar</span>
                </button>
                <button
                  onClick={() => openRejectModal(request)}
                  disabled={processingId === request.id}
                  className="flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <FiX className="h-4 w-4" />
                  <span>Rechazar</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reject Modal */}
      {rejectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Rechazar Solicitud</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Vas a rechazar la solicitud de modificacion de <strong>{selectedRequest?.requester_name}</strong>.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo del rechazo (opcional)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Escribe el motivo del rechazo..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setSelectedRequest(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={processingId === selectedRequest?.id}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Rechazar Solicitud
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModificationRequestsPanel;
